<script setup lang="ts">
import { delay } from "@fxts/core";
import {
  mdiEmailOutline,
  mdiMapMarkerOutline,
  mdiPhone,
  mdiSend,
} from "@mdi/js";
import type { Content } from "@prismicio/client";
import confetti from "canvas-confetti";

enum ObjectEnum {
  OPTION1 = "Architectural Design (건축설계)",
  OPTION2 = "Renovation (리노베이션)",
  OPTION3 = "Real Estate Purchase and Rental (부동산 구매 및 임대)",
  OPTION4 = "Interior Design (인테리어)",
  OPTION5 = "Others (기타)",
}

const props = defineProps(
  getSliceComponentProps<Content.ContactFormSlice>([
    "slice",
    "index",
    "slices",
    "context",
  ]),
);
const inputs = reactive<any>({
  name: "",
  email: "",
  location: "",
  object: "",
  content: "",
});
const errors = reactive<any>({
  name: "",
  email: "",
  location: "",
  object: "",
  content: "",
});

const isDirty = ref(false);
const REGEX_EMAIL = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;
const { t } = useI18n();
const objectOptions = Object.values(ObjectEnum).map((v, idx) => ({
  wording: t(`ContactPage.objectOptions.${idx}`),
  value: v as string,
}));

watchDebounced(
  () => inputs.name,
  (v) => {
    errors.name = v ? "" : t("ContactPage.required");
  },
  { debounce: 200, immediate: true },
);
watchDebounced(
  () => inputs.email,
  (v) => {
    if (!v) {
      errors.email = t("ContactPage.required");
      return;
    }

    errors.email = RegExp(REGEX_EMAIL).test(v) ? "" : "Invalid email";
  },
  { debounce: 200, immediate: true },
);
watchDebounced(
  () => inputs.object,
  (v) => {
    if (!v) {
      errors.object = t("ContactPage.required");
      return;
    }
    if (!Object.values(ObjectEnum).includes(v)) {
      errors.object = t("ContactPage.invalid");
      return;
    }
    errors.object = "";
  },
  { debounce: 200, immediate: true },
);
watch(
  () => inputs.content,
  (v) => {
    if (!v) {
      errors.content = t("ContactPage.required");
      return;
    }

    errors.content = v.length > 1000 ? t("ContactPage.contentTooLong") : "";
  },
  { immediate: true },
);
watchDebounced(
  () => inputs.location,
  (v) => {
    errors.location = v ? "" : t("ContactPage.required");
  },
  { debounce: 200, immediate: true },
);

const checkHasError = () => {
  for (const key in errors) {
    if (errors[key]) return true;
  }
  return false;
};

const fire = (particleRatio, opts) => {
  const count = 200;
  const defaults = {
    origin: { y: 0.7 },
  };

  confetti({
    ...defaults,
    ...opts,
    particleCount: Math.floor(count * particleRatio),
  });
};

const onSubmit = async () => {
  isDirty.value = true;
  if (checkHasError()) return;

  try {
    isSending.value = true;
    const url =
      "https://docs.google.com/forms/d/e/1FAIpQLSdzP7KDMTcrkzVTXU4xgFgQnHe0UFduusxHPKPWgWxbRmRENQ/formResponse";
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        "entry.1421730053": inputs.email,
        "entry.622659711": inputs.name,
        "entry.668309532": inputs.location,
        "entry.1156446659": inputs.object,
        "entry.760052619": inputs.content,
      }),
    });
    await delay(1500);
    Object.keys(inputs).forEach((key) => {
      inputs[key] = "";
    });
  } finally {
    isSending.value = false;
    isSentMessage.value = true;

    await delay(600);
    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    });
    fire(0.2, {
      spread: 60,
    });
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8,
    });
    fire(0.1, {
      spread: 120,
      startVelocity: 25,
      decay: 0.92,
      scalar: 1.2,
    });
    fire(0.1, {
      spread: 120,
      startVelocity: 45,
    });
  }
};
const isSending = ref(false);
const isSentMessage = ref(false);
</script>

<template>
  <section
    :data-slice-type="slice.slice_type"
    :data-slice-variation="slice.variation"
    class="w-full"
  >
    <div class="flex flex-col items-center gap-5">
      <div class="flex-1 text-2xl font-extralight -md:mt-0">
        Architect Studio Kim
      </div>
      <ul class="flex flex-col gap-2">
        <li class="text-xm flex items-center gap-4">
          <BIcon :path="mdiMapMarkerOutline" />
          {{ slice.primary.address }}
        </li>
        <li>
          <a
            class="flex items-center gap-4 hover:underline"
            :href="`mailto:${slice.primary.email}`"
          >
            <BIcon :path="mdiEmailOutline" />{{ slice.primary.email }}
          </a>
        </li>
        <li class="flex gap-4">
          <BIcon :path="mdiPhone" />
          {{ slice.primary.tel }}
        </li>
      </ul>
    </div>

    <div class="mx-auto mt-20 w-full max-w-[500px] -sm:mt-10">
      <Transition name="fade" mode="out-in">
        <form
          class="flex flex-col flex-wrap gap-4"
          @submit.prevent="onSubmit"
          novalidate
          v-if="!isSentMessage"
        >
          <BInputText
            :label="$t('ContactPage.name')"
            type="text"
            class="flex-1"
            :invalid="!!errors.name && isDirty"
            autocomplete="given-name"
            v-model="inputs.name"
            :error="isDirty ? errors.name : ''"
            required
          />
          <BInputText
            :label="$t('ContactPage.email')"
            type="email"
            class="flex-1"
            :invalid="!!errors.email && isDirty"
            autocomplete="email"
            v-model="inputs.email"
            :error="isDirty ? errors.email : ''"
            required
          />
          <BInputText
            :label="$t('ContactPage.location')"
            type="text"
            class="w-full"
            :placeholder="$t('ContactPage.locationPlaceholder')"
            :invalid="!!errors.location && isDirty"
            v-model="inputs.location"
            :error="isDirty ? errors.location : ''"
            required
          />
          <BSelect
            :label="$t('ContactPage.object')"
            type="text"
            class="w-full"
            :invalid="!!errors.object && isDirty"
            v-model="inputs.object"
            :error="isDirty ? errors.object : ''"
            :options="objectOptions"
            required
          />

          <div class="flex w-full flex-col gap-1">
            <label>
              {{ $t("ContactPage.content") }}
              <span class="text-red-500">*</span>
            </label>
            <div class="flex flex-col items-end">
              <Textarea
                class="w-full"
                v-model="inputs.content"
                rows="5"
                cols="30"
                :invalid="!!errors.content && isDirty"
              />
              <small>{{ inputs.content.length }} / 1000</small>
            </div>
            <Message
              v-show="!!errors.content && isDirty"
              size="small"
              severity="error"
              variant="simple"
            >
              {{ errors.content }}
            </Message>
          </div>

          <Button
            label="Submit"
            class="flex w-full items-center"
            size="large"
            type="submit"
            :loading="isSending"
          >
            <BIcon v-if="!isSending" :path="mdiSend" class="h-5 w-5" />
            <span v-if="!isSending">{{ $t("ContactPage.submit") }}</span>
          </Button>
        </form>
        <div
          v-else
          class="flex min-h-[300px] flex-col items-center justify-center text-center"
        >
          <h1 class="text-2xl font-extralight">
            {{ $t("ContactPage.thanks") }}
          </h1>
          <p class="mt-4">{{ $t("ContactPage.subSuccessMessage") }}</p>
        </div>
      </Transition>
    </div>
  </section>
</template>

<style scoped>
.fade-enter-active {
  transition: all 0.8s ease;
}

.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  scale: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  scale: 1;
}
</style>
